import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus, Search, Filter, Calendar, Users, Trophy, Eye, Edit, Trash2, TrendingUp, RefreshCw } from 'lucide-react';
import { hackathonAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface Hackathon {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  status: string;
  maxTeams: number;
  currentTeams: number;
  phases: Array<{
    _id: string;
    name: string;
    status: string;
  }>;
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
  isActive: boolean;
}

const HackathonManagement: React.FC = () => {
  const { success, error } = useToast();
  const location = useLocation();
  const [hackathons, setHackathons] = useState<Hackathon[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [hackathonToDelete, setHackathonToDelete] = useState<{id: string, title: string} | null>(null);

  useEffect(() => {
    fetchHackathons();
    fetchStats();
  }, [currentPage, searchTerm, statusFilter]);

  // Check if we need to refresh after navigation
  useEffect(() => {
    if (location.state?.refresh) {
      fetchHackathons();
      fetchStats();
      // Clear the refresh state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchHackathons = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: 10
      };
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      
      const response = await hackathonAPI.getHackathons(params);
      setHackathons(response.hackathons);
      setTotalPages(response.pagination.total);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch hackathons');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await hackathonAPI.getHackathonStats();
      setStats(response);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleDelete = (hackathonId: string, hackathonTitle: string) => {
    setHackathonToDelete({ id: hackathonId, title: hackathonTitle });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!hackathonToDelete) return;

    try {
      await hackathonAPI.deleteHackathon(hackathonToDelete.id);
      success('Hackathon deleted successfully');
      fetchHackathons();
      fetchStats();
      setShowDeleteDialog(false);
      setHackathonToDelete(null);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to delete hackathon');
      setShowDeleteDialog(false);
      setHackathonToDelete(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'registration_open': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getActivePhases = (phases: any[]) => {
    return phases.filter(phase => phase.status === 'active').length;
  };

  if (loading && !hackathons.length) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header with strategic whitespace */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Hackathon Management
            </h1>
            <p className="text-gray-600">
              Manage and monitor all hackathons in the platform
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/hackathons/create">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2">
                <Plus className="h-4 w-4 mr-2" />
                Create Hackathon
              </Button>
            </Link>
            <Link to="/admin/teams">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2">
                <Users className="h-4 w-4 mr-2" />
                Team Management
              </Button>
            </Link>
            <Button
              onClick={fetchHackathons}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Statistics Cards - Visual hierarchy with color accents */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Hackathons</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  </div>
                  <Trophy className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Upcoming</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.upcoming}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">By Status</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
                  </div>
                  <Filter className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Search and Filter - Intuitive navigation */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search hackathons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="registration_open">Registration Open</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Hackathons List - Content-first layout */}
      <div className="space-y-4">
        {hackathons.map((hackathon) => (
          <Card key={hackathon._id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {hackathon.title}
                    </h3>
                    <Badge className={getStatusColor(hackathon.status)}>
                      {hackathon.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {!hackathon.isActive && (
                      <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                    )}
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {hackathon.description}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(hackathon.startDate)} - {formatDate(hackathon.endDate)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{hackathon.currentTeams}/{hackathon.maxTeams} teams</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4" />
                      <span>{getActivePhases(hackathon.phases)} active phases</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Created by: {hackathon.createdBy.firstName} {hackathon.createdBy.lastName}</span>
                    </div>
                  </div>
                </div>
                
                {/* Action buttons - Feedback responsiveness */}
                <div className="flex items-center gap-2 ml-4">
                  <Link to={`/admin/hackathons/${hackathon._id}/view`}>
                    <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to={`/admin/hackathons/${hackathon._id}/edit`}>
                    <Button variant="outline" size="sm" className="text-green-600 hover:text-green-700">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(hackathon._id, hackathon.title)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination - Motion choreography */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {hackathons.length === 0 && !loading && (
        <div className="text-center py-12">
          <Trophy className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hackathons found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first hackathon.'}
          </p>
          {!searchTerm && !statusFilter && (
            <Link to="/admin/hackathons/create">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Create First Hackathon
              </Button>
            </Link>
          )}
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Hackathon</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete the hackathon <strong>{hackathonToDelete?.title}</strong>?</p>
            <p className="text-sm text-gray-600 mt-2">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HackathonManagement; 